import React, { useEffect, useState } from 'react';
import { MapPin, Database, Clock, Activity, Waves, Thermometer, Map } from 'lucide-react';

const StationTooltip = ({
  station,
  showDetails = true,
  showDataPreview = false,
  timeSeriesData = [],
  currentFrame = 0,
  className = ""
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Handle visibility and positioning
  useEffect(() => {
    if (station) {
      setIsVisible(true);
      setPosition({ x: station.x, y: station.y });
    } else {
      setIsVisible(false);
    }
  }, [station]);

  // Get current data for this station if available
  const getCurrentStationData = () => {
    if (!timeSeriesData.length || !station) return null;

    const currentData = timeSeriesData[currentFrame % timeSeriesData.length];
    return currentData;
  };

  // Format coordinate display
  const formatCoordinate = (lat, lng) => {
    const latDir = lat >= 0 ? 'N' : 'S';
    const lngDir = lng >= 0 ? 'E' : 'W';
    return `${Math.abs(lat).toFixed(4)}°${latDir}, ${Math.abs(lng).toFixed(4)}°${lngDir}`;
  };

  // Get station type styling
  const getStationTypeInfo = (type) => {
    switch (type?.toLowerCase()) {
      case 'usm':
        return {
          color: 'text-blue-400',
          bgColor: 'bg-blue-400/10',
          label: 'USM Station'
        };
      case 'ndbc':
        return {
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-400/10',
          label: 'NDBC Buoy'
        };
      case 'api_station':
        return {
          color: 'text-green-400',
          bgColor: 'bg-green-400/10',
          label: 'Data Station'
        };
      // *** NEWLY ADDED for SSH Layer ***
      case 'ssh':
        return {
          color: 'text-indigo-400',
          bgColor: 'bg-indigo-400/10',
          label: 'Surface Elevation'
        };
      default:
        return {
          color: 'text-slate-400',
          bgColor: 'bg-slate-400/10',
          label: 'Unknown'
        };
    }
  };

  // Format data count display
  const formatDataCount = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  // Get time range display
  const getTimeRangeDisplay = (station) => {
    if (!station.timeRange) return null;

    const { start, end } = station.timeRange;
    const duration = end - start;
    const days = Math.floor(duration / (1000 * 60 * 60 * 24));

    return {
      start: start.toLocaleDateString(),
      end: end.toLocaleDateString(),
      duration: days > 0 ? `${days} days` : 'Same day'
    };
  };

  // Check if this is a generic info tooltip (for coordinates, grid, POV, or layers like SSH)
  // We identify these by either a specific flag (e.g., isCoordinate) or the absence of a 'type' property.
  const isInfoTooltip = station?.isCoordinate || station?.isPOV || station?.isGrid || station?.isWind || !station?.type;

  if (!isVisible || !station) return null;

  const stationTypeInfo = getStationTypeInfo(station.type);
  const currentData = getCurrentStationData();
  const timeRange = getTimeRangeDisplay(station);

  // Render info/data availability tooltip
  if (isInfoTooltip) {
    return (
      <div
        className={`absolute pointer-events-none bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 transition-opacity duration-200 ${className} dark:bg-slate-800/95 dark:border-cyan-400/50`}
        style={{
          left: position.x + 15,
          top: position.y - 10,
          transform: 'translateY(-100%)',
          maxWidth: '260px'
        }}
      >
        {/* Header */}
        <div className="p-3 border-b border-slate-200 dark:border-slate-700 dark:border-slate-700/50">
          <div className="flex items-center gap-2 mb-1">
            {station.isPOV && <div className="w-4 h-4 rounded-full bg-green-400"></div>}
            {station.isGrid && <Map className="w-4 h-4 text-blue-600 dark:text-blue-400 dark:text-blue-400" />}
            {station.isWind && <Waves className="w-4 h-4 text-cyan-600 dark:text-cyan-300 dark:text-cyan-400" />}
            {station.isCoordinate && <MapPin className="w-4 h-4 text-cyan-600 dark:text-cyan-300 dark:text-cyan-400" />}
            {/* Fallback icon for other info tooltips like Surface Elevation */}
            {!station.isPOV && !station.isGrid && !station.isWind && !station.isCoordinate && <Waves className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
            <div className="font-semibold text-blue-700 dark:text-blue-300 text-sm dark:text-cyan-300">{station.name}</div>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 space-y-2">
          {/* Coordinates for coordinate tooltip */}
          {station.isCoordinate && station.longitude && station.latitude && (
            <div className="text-xs">
              <div className="text-slate-500 dark:text-slate-400 mb-1 dark:text-slate-400">Coordinates</div>
              <div className="text-slate-700 dark:text-slate-200 font-mono dark:text-slate-200">
                {formatCoordinate(station.latitude, station.longitude)}
              </div>
            </div>
          )}

          {/* Available data or details */}
          {station.details && (
            <div className="text-xs">
              <div className="text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1 dark:text-slate-400">
                <Database className="w-3 h-3" />
                {station.isCoordinate ? 'Available Data' : 'Details'}
              </div>
              <div className="text-slate-700 dark:text-slate-200 space-y-1 dark:text-slate-200">
                {station.details.split('\n').map((line, index) => {
                  if (line.includes('Available data:')) return null;
                  return (
                    <div key={index} className="flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-cyan-400"></div>
                      {line}
                    </div>
                  );
                }).filter(Boolean)}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {station.isCoordinate && (
          <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-center">
            <div className="text-xs text-slate-500 dark:text-slate-400">Click for details</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Data within 0.1° radius
            </div>
          </div>
        )}

        {/* Tooltip arrow */}
        <div
          className="absolute w-2 h-2 bg-white dark:bg-slate-900 border-l border-b border-slate-200 dark:border-slate-700 transform rotate-45 dark:bg-slate-800 dark:border-cyan-400/50"
          style={{
            left: '-4px',
            top: '20px'
          }}
        ></div>
      </div>
    );
  }

  // Render standard station tooltip
  return (
    <div
      className={`absolute pointer-events-none bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 transition-opacity duration-200 ${className} dark:border-blue-400/50 dark:bg-slate-800/95`}
      style={{
        left: position.x + 15,
        top: position.y - 10,
        transform: 'translateY(-100%)',
        maxWidth: '280px'
      }}
    >
      {/* Header */}
      <div className="p-3 border-b border-slate-200 dark:border-slate-700 dark:border-slate-700/50">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400 dark:text-blue-400" />
          <div className="font-semibold text-blue-700 dark:text-blue-300 text-sm dark:text-blue-300">{station.name}</div>
        </div>
        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${stationTypeInfo.bgColor} ${stationTypeInfo.color}`}>
          <div className="w-1.5 h-1.5 rounded-full bg-current"></div>
          {stationTypeInfo.label}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-3 space-y-2">

        {/* Coordinates */}
        <div className="text-xs">
          <div className="text-slate-500 dark:text-slate-400 mb-1 dark:text-slate-400">Location</div>
          <div className="text-slate-700 dark:text-slate-200 font-mono dark:text-slate-200">
            {station.coordinates ? formatCoordinate(station.coordinates[1], station.coordinates[0]) : 'Unknown location'}
          </div>
        </div>

        {/* *** NEWLY ADDED Generic Details Section *** */}
        {/* This will now render details for SSH or other simple data types */}
        {station.details && (
          <div className="text-xs">
            <div className="text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1 dark:text-slate-400">
              <Database className="w-3 h-3" />
              Details
            </div>
            <div className="text-slate-700 dark:text-slate-200 space-y-1 dark:text-slate-200">
              {station.details.split('\n').map((line, index) => {
                const parts = line.split(':');
                return (
                  <div key={index} className="flex justify-between items-center">
                    <span>{parts[0]}</span>
                    {parts.length > 1 && <span className="font-mono font-semibold">{parts[1].trim()}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {showDetails && (
          <>
            {/* Data Points */}
            {station.dataPoints && (
              <div className="text-xs">
                <div className="text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1 dark:text-slate-400">
                  <Database className="w-3 h-3" />
                  Data Points
                </div>
                <div className="text-slate-700 dark:text-slate-200 dark:text-slate-200">
                  {formatDataCount(station.dataPoints)} measurements
                </div>
              </div>
            )}

            {/* Time Range */}
            {timeRange && (
              <div className="text-xs">
                <div className="text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1 dark:text-slate-400">
                  <Clock className="w-3 h-3" />
                  Time Range
                </div>
                <div className="text-slate-700 dark:text-slate-200 dark:text-slate-200">
                  <div>{timeRange.start} - {timeRange.end}</div>
                  <div className="text-slate-500 dark:text-slate-400 dark:text-slate-400">({timeRange.duration})</div>
                </div>
              </div>
            )}

            {/* Source Files */}
            {station.sourceFiles && station.sourceFiles.length > 0 && (
              <div className="text-xs">
                <div className="text-slate-500 dark:text-slate-400 mb-1 dark:text-slate-400">Data Sources</div>
                <div className="text-slate-700 dark:text-slate-200 space-y-0.5 dark:text-slate-200">
                  {station.sourceFiles.slice(0, 3).map((file, index) => (
                    <div key={index} className="truncate font-mono text-xs">
                      {file}
                    </div>
                  ))}
                  {station.sourceFiles.length > 3 && (
                    <div className="text-slate-500 dark:text-slate-400 dark:text-slate-400">
                      +{station.sourceFiles.length - 3} more files
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Available Parameters */}
            {station.availableParameters && station.availableParameters.length > 0 && (
              <div className="text-xs">
                <div className="text-slate-500 dark:text-slate-400 mb-1 dark:text-slate-400">Available Parameters</div>
                <div className="flex flex-wrap gap-1">
                  {station.availableParameters.slice(0, 4).map((param, index) => (
                    <span
                      key={index}
                      className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded text-xs dark:bg-slate-700/50 dark:text-slate-300"
                    >
                      {param}
                    </span>
                  ))}
                  {station.availableParameters.length > 4 && (
                    <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded text-xs dark:bg-slate-700/50 dark:text-slate-400">
                      +{station.availableParameters.length - 4}
                    </span>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* Current Data Preview */}
        {showDataPreview && currentData && (
          <div className="border-t border-slate-700/50 pt-2 mt-2">
            <div className="text-slate-400 mb-2 text-xs flex items-center gap-1">
              <Activity className="w-3 h-3" />
              Current Readings
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {currentData.currentSpeed && (
                <div className="flex items-center gap-1">
                  <Waves className="w-3 h-3 text-cyan-400" />
                  <div>
                    <div className="text-slate-400">Current</div>
                    <div className="text-slate-700 dark:text-slate-200 dark:text-slate-200">{currentData.currentSpeed} m/s</div>
                  </div>
                </div>
              )}
              {currentData.temperature && (
                <div className="flex items-center gap-1">
                  <Thermometer className="w-3 h-3 text-red-400" />
                  <div>
                    <div className="text-slate-400">Temp</div>
                    <div className="text-slate-700 dark:text-slate-200 dark:text-slate-200">{currentData.temperature}°F</div>
                  </div>
                </div>
              )}
              {currentData.waveHeight && (
                <div className="flex items-center gap-1">
                  <Waves className="w-3 h-3 text-blue-400" />
                  <div>
                    <div className="text-slate-400">Wave</div>
                    <div className="text-slate-700 dark:text-slate-200 dark:text-slate-200">{currentData.waveHeight} m</div>
                  </div>
                </div>
              )}
              {currentData.salinity && (
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  <div>
                    <div className="text-slate-400">Salinity</div>
                    <div className="text-slate-700 dark:text-slate-200 dark:text-slate-200">{currentData.salinity} PSU</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tooltip arrow */}
      <div
        className="absolute w-2 h-2 bg-white dark:bg-slate-900 border-l border-b border-slate-200 dark:border-slate-700 transform rotate-45 dark:bg-slate-800 dark:border-blue-400/50"
        style={{
          left: '-4px',
          top: '20px'
        }}
      ></div>
    </div>
  );
};

// Simplified version for basic use cases
export const SimpleStationTooltip = ({ station }) => {
  if (!station) return null;

  return (
    <div
      className="absolute pointer-events-none bg-white/95 dark:bg-slate-800/95 border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-sm z-50 shadow-lg dark:bg-slate-800/95 dark:border-blue-400/50"
      style={{
        left: station.x + 10,
        top: station.y - 10,
        transform: 'translateY(-100%)'
      }}
    >
      <div className="font-semibold text-blue-700 dark:text-blue-300 mb-2 dark:text-blue-300">{station.name}</div>
      <div className="space-y-1 text-xs">
        <div className="text-slate-600 dark:text-slate-300 dark:text-slate-300">
          <span className="text-slate-400">Coordinates:</span>
          <br />{station.coordinates[1]}, {station.coordinates[0]}
        </div>
        {station.dataPoints && (
          <div className="text-slate-600 dark:text-slate-300 dark:text-slate-300">
            <span className="text-slate-400">Data Points:</span> {station.dataPoints}
          </div>
        )}
        <div className="text-slate-600 dark:text-slate-300 dark:text-slate-300">
          <span className="text-slate-400">Type:</span> {station.type}
        </div>
      </div>
    </div>
  );
};

export default StationTooltip;